import { supabase } from "@/integrations/supabase/client";

export class SupabaseStorage {
  private static readonly BUCKET_NAME = "images";
  
  /**
   * Upload an image to Supabase storage
   * @param file The file to upload
   * @param path The path within the bucket (e.g., "profiles/user-id/avatar.jpg")
   * @returns The public URL of the uploaded image
   */
  static async uploadImage(file: File, path: string): Promise<string | null> {
    try {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        throw new Error("File must be an image");
      }
      
      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        throw new Error("File size must be less than 5MB");
      }
      
      // Upload the file
      const { data, error } = await supabase.storage
        .from(this.BUCKET_NAME)
        .upload(path, file, {
          cacheControl: "3600",
          upsert: true, // Replace if exists
        });
      
      if (error) {
        console.error("Error uploading image:", error);
        throw error;
      }
      
      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from(this.BUCKET_NAME)
        .getPublicUrl(path);
      
      return publicUrl;
    } catch (error) {
      console.error("Error in uploadImage:", error);
      throw error;
    }
  }
  
  /**
   * Delete an image from Supabase storage
   * @param path The path of the image to delete
   */
  static async deleteImage(path: string): Promise<boolean> {
    try {
      const { error } = await supabase.storage
        .from(this.BUCKET_NAME)
        .remove([path]);
      
      if (error) {
        console.error("Error deleting image:", error);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error("Error in deleteImage:", error);
      return false;
    }
  }
  
  /**
   * Generate a unique path for profile images
   * @param userId The user's ID
   * @param type The type of image (avatar, background, etc.)
   */
  static generateProfileImagePath(userId: string, type: "avatar" | "background"): string {
    const timestamp = Date.now();
    const extension = "jpg"; // We'll convert all images to jpg
    return `profiles/${userId}/${type}-${timestamp}.${extension}`;
  }
  
  /**
   * Generate a unique path for link icons
   * @param userId The user's ID
   * @param linkId The link's ID
   */
  static generateLinkIconPath(userId: string, linkId: string): string {
    const timestamp = Date.now();
    return `links/${userId}/${linkId}-${timestamp}.png`;
  }
  
  /**
   * Extract the storage path from a Supabase storage URL
   * @param url The full URL
   * @returns The path within the bucket
   */
  static getPathFromUrl(url: string): string | null {
    if (!url.includes("/storage/v1/object/public/")) {
      return null;
    }
    
    const parts = url.split("/storage/v1/object/public/" + this.BUCKET_NAME + "/");
    return parts[1] || null;
  }
  
  /**
   * Upload an image from a URL (for migrating existing images)
   * @param imageUrl The URL of the image to download and upload
   * @param path The path within the bucket
   */
  static async uploadImageFromUrl(imageUrl: string, path: string): Promise<string | null> {
    try {
      // Fetch the image
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error("Failed to fetch image");
      }
      
      // Convert to blob
      const blob = await response.blob();
      
      // Create a File object
      const file = new File([blob], "image.jpg", { type: blob.type });
      
      // Upload using the regular method
      return this.uploadImage(file, path);
    } catch (error) {
      console.error("Error uploading image from URL:", error);
      return null;
    }
  }
}